// Collapsible footer that surfaces what the AI just did. The HF
// token is held server-side by the Pages Function proxy, so the
// panel doesn't ask the user for one — it's purely a read-out of
// the most recent call.

import { useEffect, useRef, useState } from "react";
import {
  getLastAiCall,
  subscribeLastAiCall,
  type AiCallRecord,
} from "../lib/ai-responder";
import { useLoopState } from "../lib/use-loop-state";
import { formatPredicate } from "../lib/dsl";
import type { ProposalStatus } from "../lib/andon-loop";

export type ResponderMode = "stub" | "ai-busy" | "ai-ok" | "ai-error";

let mode: ResponderMode = "stub";
const modeListeners = new Set<(m: ResponderMode) => void>();

export function setResponderMode(next: ResponderMode): void {
  mode = next;
  for (const fn of modeListeners) {
    try { fn(mode); } catch (e) { console.warn("[panel] mode listener:", e); }
  }
}

export function getResponderMode(): ResponderMode {
  return mode;
}

function useResponderMode(): ResponderMode {
  const [m, setM] = useState<ResponderMode>(mode);
  useEffect(() => {
    const fn = (next: ResponderMode) => setM(next);
    modeListeners.add(fn);
    return () => { modeListeners.delete(fn); };
  }, []);
  return m;
}

function useLastAiCall(): AiCallRecord | null {
  const [rec, setRec] = useState<AiCallRecord | null>(getLastAiCall());
  useEffect(() => subscribeLastAiCall(setRec), []);
  return rec;
}

const MODE_META: Record<ResponderMode, { label: string; color: string }> = {
  "stub":     { label: "STUB",     color: "var(--fg-2)" },
  "ai-busy":  { label: "AI BUSY",  color: "var(--caution-yellow)" },
  "ai-ok":    { label: "AI OK",    color: "var(--safety-green)" },
  "ai-error": { label: "AI ERROR", color: "var(--andon-red)" },
};

export function StatusBadge() {
  const m = useResponderMode();
  const meta = MODE_META[m];
  return (
    <span
      className="status-badge"
      style={{ borderColor: meta.color, color: meta.color }}
      title={`Responder mode: ${meta.label}`}
    >
      <span className="status-dot" style={{ background: meta.color }} />
      {meta.label}
      <style>{`
        .status-badge {
          display: inline-flex; align-items: center; gap: 6px;
          font-family: var(--font-mono);
          font-size: 11px;
          font-weight: 700;
          padding: 2px 10px;
          border: 2px solid currentColor;
          border-radius: 999px;
          background: var(--bg-1);
        }
        .status-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
        }
      `}</style>
    </span>
  );
}

// How the harness ultimately ruled on a proposal — surfaced next to what
// the AI proposed, so the two halves of the loop read as one sentence.
const VERDICT_META: Record<ProposalStatus, { label: string; color: string } | null> = {
  proposed:            { label: "IN PIPELINE", color: "var(--caution-yellow)" },
  tier_verified:       { label: "IN PIPELINE", color: "var(--caution-yellow)" },
  tested:              { label: "IN PIPELINE", color: "var(--caution-yellow)" },
  deployed:            { label: "DEPLOYED",    color: "var(--safety-green)" },
  tier_rejected:       { label: "REJECTED",    color: "var(--andon-red)" },
  rejected_by_sandbox: { label: "REJECTED",    color: "var(--andon-red)" },
};

export function AiThinkingPanel() {
  const [expanded, setExpanded] = useState(false);
  const rec = useLastAiCall();
  const mode = useResponderMode();
  const state = useLoopState();

  const latest = state.proposals[state.proposals.length - 1] ?? null;
  const verdict = latest ? VERDICT_META[latest.status] : null;

  // Pulse the bar each time a new proposal lands (works in stub mode too,
  // where there's no AI call but the loop still proposes + gets judged).
  const [pulse, setPulse] = useState(false);
  const lastCount = useRef(0);
  useEffect(() => {
    if (state.proposals.length > lastCount.current) {
      lastCount.current = state.proposals.length;
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 700);
      return () => clearTimeout(t);
    }
  }, [state.proposals.length]);

  // Prefer the AI call's own parse; fall back to the latest proposal (stub).
  const proposedText = rec?.parsed
    ? `${rec.parsed.name} { ${formatPredicate(rec.parsed.predicate)} }`
    : latest
      ? `${latest.newRule.name} { ${formatPredicate(latest.newRule.predicate)} }`
      : null;

  const lastSummary = rec?.error
    ? `error: ${rec.error}`
    : proposedText
      ? `${proposedText}${rec ? ` · ${rec.model ?? "?"} · ${Math.round(rec.latencyMs)}ms` : " · stub"}`
      : mode === "ai-busy"
        ? "in flight…"
        : mode === "stub"
          ? "loop running in stub mode — pull the cord to see a proposal."
          : "no calls yet — pull the cord to see the AI propose.";

  return (
    <section className={`atp ${expanded ? "atp-open" : ""} ${pulse ? "atp-pulse" : ""}`}>
      <button
        type="button"
        className="atp-toggle"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="atp-chev">{expanded ? "▾" : "▸"}</span>
        <span className="atp-label">AI THINKING</span>
        <span className="atp-summary">{lastSummary}</span>
        {verdict && (
          <span
            className="atp-verdict"
            style={{ color: verdict.color, borderColor: verdict.color }}
          >
            {verdict.label}
          </span>
        )}
      </button>

      {expanded && (
        <div className="atp-body">
          <div className="atp-note">
            BabyAI calls go through this deploy's <code>/api/babyai/*</code>{" "}
            Pages Function, which attaches the HF token server-side and
            forwards to <code>novasynchris-babyai.hf.space</code>. No token
            ever reaches the browser. When the proxy is unreachable (e.g.
            local <code>vite</code> dev), the loop falls back to the
            deterministic stub responder so the demo still runs.
          </div>

          {rec ? (
            <>
              <div className="atp-section-head">last call</div>
              <div className="atp-meta">
                {new Date(rec.ts).toLocaleString()} · model: {rec.model ?? "?"} · {Math.round(rec.latencyMs)}ms
                {rec.error && <span className="atp-err"> · {rec.error}</span>}
              </div>

              <details className="atp-detail">
                <summary>system prompt</summary>
                <pre className="atp-pre">{rec.prompt.system}</pre>
              </details>
              <details className="atp-detail">
                <summary>user prompt</summary>
                <pre className="atp-pre">{rec.prompt.user}</pre>
              </details>
              <details className="atp-detail" open>
                <summary>raw response</summary>
                <pre className="atp-pre">{rec.responseText || "(empty)"}</pre>
              </details>
              {rec.parsed && (
                <details className="atp-detail" open>
                  <summary>parsed rule</summary>
                  <pre className="atp-pre">{JSON.stringify(rec.parsed, null, 2)}</pre>
                </details>
              )}
            </>
          ) : (
            <div className="atp-empty">
              no AI calls yet. pull the cord to fire one.
            </div>
          )}
        </div>
      )}

      <Styles />
    </section>
  );
}

function Styles() {
  return (
    <style>{`
      .atp {
        border-top: 2px solid var(--border);
        background: var(--bg-2);
        max-height: 50vh;
        overflow: hidden;
        display: flex; flex-direction: column;
      }
      .atp.atp-open { max-height: 50vh; }
      .atp-pulse { animation: atp-flash 0.7s ease; }
      @keyframes atp-flash {
        0%   { background: var(--caution-yellow-soft); }
        100% { background: var(--bg-2); }
      }
      .atp-verdict {
        flex: 0 0 auto;
        font-family: var(--font-mono);
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.06em;
        padding: 1px 8px;
        border: 1.5px solid currentColor;
        border-radius: 999px;
        background: var(--bg-1);
      }
      .atp-toggle {
        display: flex; align-items: center; gap: 8px;
        background: transparent;
        border: none;
        border-radius: 0;
        padding: var(--space-2) var(--space-4);
        box-shadow: none;
        cursor: pointer;
        text-align: left;
        font-family: var(--font-mono);
        font-size: 12px;
        font-weight: 600;
        color: var(--fg-1);
      }
      .atp-toggle:hover { color: var(--fg-0); transform: none; }
      .atp-chev { color: var(--blueprint); width: 12px; }
      .atp-label {
        font-family: var(--font-display);
        font-weight: 800;
        letter-spacing: 0.1em;
        font-size: 11px;
        color: var(--fg-0);
      }
      .atp-summary {
        color: var(--fg-2);
        font-weight: 400;
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .atp-body {
        overflow-y: auto;
        padding: var(--space-3) var(--space-4) var(--space-4);
        border-top: 1px dashed var(--border-soft);
        font-family: var(--font-mono);
        font-size: 12px;
      }
      .atp-note {
        color: var(--fg-1);
        font-size: 11px;
        line-height: 1.55;
        margin-bottom: var(--space-3);
        padding: var(--space-2) var(--space-3);
        background: var(--bg-1);
        border: 1px dashed var(--border-soft);
        border-radius: var(--radius-1);
      }
      .atp-note code {
        background: var(--bg-2);
        padding: 1px 4px;
        border-radius: 3px;
      }
      .atp-section-head {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: 11px;
        letter-spacing: 0.1em;
        color: var(--fg-0);
        margin-bottom: 2px;
      }
      .atp-meta {
        font-size: 11px;
        color: var(--fg-2);
        margin-bottom: var(--space-2);
      }
      .atp-err {
        color: var(--andon-red);
        font-weight: 700;
      }
      .atp-detail {
        margin-top: 4px;
        background: var(--bg-1);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-1);
        padding: 2px 8px;
      }
      .atp-detail summary {
        font-size: 11px;
        cursor: pointer;
        font-weight: 600;
        color: var(--blueprint);
        padding: 4px 0;
      }
      .atp-detail summary:hover { color: var(--fg-0); }
      .atp-pre {
        margin: 4px 0 8px 0;
        white-space: pre-wrap;
        word-break: break-word;
        font-size: 11px;
        line-height: 1.5;
        color: var(--fg-0);
        background: #fff;
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-1);
        padding: var(--space-2);
        max-height: 200px;
        overflow-y: auto;
      }
      .atp-empty {
        color: var(--fg-2);
        font-style: italic;
        font-size: 12px;
        padding: var(--space-3) 0;
      }
    `}</style>
  );
}
